import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { track } from '../api/events.js';
import { PageTitle } from '../components/Layouts.js';
import { InvoiceStatus } from '../components/Invoice.js';
import { formatMoney, formatPrice } from '../components/site.js';
import {
  Button,
  Card,
  Checkbox,
  LinkButton,
  LoadError,
  Loading,
  Notice,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate } from '../i18n.js';
import { useMe } from '../session.js';

type Interval = 'month' | 'year';
type PaidPlan = 'starter' | 'business' | 'agency';

/** Opens a Stripe page (Checkout or Portal) in this tab. */
const go = (url: string) => {
  window.location.assign(url);
};

function PlanTable({ billing, plans }: { billing: Schemas['Billing']; plans: Schemas['Plan'][] }) {
  const { t, i18n } = useTranslation();
  const [interval, setInterval] = useState<Interval>('month');
  const checkout = useMutation({
    mutationFn: (planCode: PaidPlan) =>
      unwrap(api.POST('/billing/checkout-session', { body: { planCode, interval } })),
    onSuccess: (data) => go(data.url),
  });
  const invoiceMode = billing.mode === 'invoice';
  const subscribed = billing.subscription !== null;
  const checkoutUrl = (plan: string) => `/billing/checkout?plan=${plan}&interval=${interval}`;
  const rows: [string, (p: Schemas['Plan']) => string][] = [
    ['sites', (p) => String(p.limits.maxSites)],
    ['interval', (p) => t('billing.minutes', { count: p.limits.minIntervalSeconds / 60 })],
    ['pages', (p) => String(p.limits.crawlMaxPages)],
    ['recipients', (p) => String(p.limits.alertRecipients)],
    ['keywords', (p) => String(p.limits.keywordMonitorsPerSite)],
    ['digest', (p) => (p.limits.weeklyDigest ? '✓' : '–')],
    ['report', (p) => (p.limits.siteReport ? '✓' : '–')],
    ['history', (p) => t('billing.days', { count: p.limits.historyDays })],
    ['discount', (p) => (p.limits.serviceDiscountPct ? `−${p.limits.serviceDiscountPct} %` : '–')],
  ];
  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">{t('billing.compare')}</h2>
        <div role="group" aria-label={t('billing.intervalLabel')} className="flex gap-1">
          {(['month', 'year'] as const).map((i) => (
            <button
              key={i}
              type="button"
              aria-pressed={interval === i}
              onClick={() => setInterval(i)}
              className={`min-h-11 rounded-md border px-3 font-semibold ${interval === i ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
            >
              {t(`billing.intervals.${i}`)}
            </button>
          ))}
        </div>
      </div>
      {checkout.isError ? (
        <Notice kind="error">
          {checkout.error instanceof ApiError && checkout.error.code === 'UNAVAILABLE'
            ? t('billing.unavailable')
            : errorMessage(checkout.error, t)}
        </Notice>
      ) : null}
      <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {plans.map((p) => {
          const current = p.code === billing.planCode;
          const price = interval === 'month' ? p.priceMonthCents : p.priceYearCents;
          return (
            <li
              key={p.code}
              className={`flex flex-col gap-2 rounded-lg border p-4 ${current ? 'border-blue-700 bg-blue-50' : 'border-slate-200 bg-white'}`}
            >
              <h3 className="text-lg font-bold">{t(`admin.plans.${p.code}`)}</h3>
              <p className="text-2xl font-bold">
                {formatPrice(price, i18n.language)}
                <span className="text-base font-normal text-slate-700">
                  {' '}
                  / {t(`billing.per.${interval}`)}
                </span>
              </p>
              <p className="text-sm text-slate-700">{t('billing.net')}</p>
              <dl className="flex flex-col gap-1 text-sm">
                {rows.map(([key, value]) => (
                  <div key={key} className="flex justify-between gap-2">
                    <dt className="text-slate-700">{t(`billing.rows.${key}`)}</dt>
                    <dd className="font-semibold">{value(p)}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-auto flex flex-col gap-2 pt-2">
                {current ? (
                  <p className="font-semibold text-blue-900">{t('billing.current')}</p>
                ) : null}
                {invoiceMode && p.code !== 'free' ? (
                  <LinkButton
                    to={checkoutUrl(p.code)}
                    variant={current ? 'secondary' : 'primary'}
                    onClick={() => track('upgrade_clicked', { plan: p.code, source: 'billing' })}
                  >
                    {current ? t('billing.renew') : t('billing.choose')}
                  </LinkButton>
                ) : !current && p.code !== 'free' && !subscribed ? (
                  <Button
                    busy={checkout.isPending && checkout.variables === p.code}
                    disabled={!billing.billingEnabled}
                    onClick={() => {
                      track('upgrade_clicked', { plan: p.code, source: 'billing' });
                      checkout.mutate(p.code as PaidPlan);
                    }}
                  >
                    {t('billing.choose')}
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {subscribed && !invoiceMode ? (
        <p className="mt-3 text-sm text-slate-700">{t('billing.changeInPortal')}</p>
      ) : null}
      {invoiceMode ? (
        <p className="mt-3 text-sm text-slate-700">{t('billing.invoiceHint')}</p>
      ) : null}
    </Card>
  );
}

function ActiveSites({ billing }: { billing: Schemas['Billing'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [chosen, setChosen] = useState<string[]>(
    billing.sites.filter((s) => s.status === 'active').map((s) => s.id),
  );
  const save = useMutation({
    mutationFn: () => unwrap(api.POST('/billing/active-sites', { body: { siteIds: chosen } })),
    onSuccess: (data) => {
      client.setQueryData(['billing'], data);
      void client.invalidateQueries({ queryKey: ['sites'] });
    },
  });
  const max = billing.usage.maxSites;
  return (
    <Card>
      <h2 className="mb-1 text-xl font-semibold">{t('billing.activeTitle')}</h2>
      <p className="mb-3 text-slate-800">{t('billing.activeText', { max })}</p>
      <ul className="flex flex-col gap-2">
        {billing.sites.map((s) => (
          <li key={s.id}>
            <Checkbox
              label={
                <span className="break-all">
                  <strong>{s.name}</strong> · {s.url}
                  {s.pausedByPlan ? ` · ${t('billing.pausedByPlan')}` : ''}
                </span>
              }
              checked={chosen.includes(s.id)}
              disabled={!chosen.includes(s.id) && chosen.length >= max}
              onChange={(e) =>
                setChosen(e.target.checked ? [...chosen, s.id] : chosen.filter((id) => id !== s.id))
              }
            />
          </li>
        ))}
      </ul>
      {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
      {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
      <Button
        className="mt-3"
        busy={save.isPending}
        disabled={chosen.length === 0}
        onClick={() => save.mutate()}
      >
        {t('billing.activeSave')}
      </Button>
    </Card>
  );
}

function Invoices({ timeZone }: { timeZone: string | undefined }) {
  const { t, i18n } = useTranslation();
  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => unwrap(api.GET('/billing/invoices')),
  });
  if (invoices.isPending) return null;
  if (invoices.isError)
    return (
      <LoadError
        message={errorMessage(invoices.error, t)}
        onRetry={() => void invoices.refetch()}
      />
    );
  return (
    <Card>
      <h2 className="mb-3 text-xl font-semibold">{t('billing.invoices')}</h2>
      {invoices.data.items.length === 0 ? <p>{t('billing.noInvoices')}</p> : null}
      <ul className="flex flex-col divide-y divide-slate-200">
        {invoices.data.items.map((inv) => (
          <li key={inv.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
            <span className="min-w-0 flex-1">
              <span className="block font-mono font-semibold">{inv.number}</span>
              <span className="block text-sm text-slate-700">
                {inv.description} · {formatDate(inv.issuedAt, i18n.language, timeZone)}
              </span>
            </span>
            <span className="font-semibold tabular-nums">
              {formatMoney(inv.grossCents, i18n.language)}
            </span>
            <InvoiceStatus status={inv.status} />
            <LinkButton to={`/billing/invoices/${inv.id}`} variant="secondary" size="sm">
              {inv.status === 'open' ? t('billing.payInvoice') : t('billing.showInvoice')}
            </LinkButton>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** S11 "Tarif & Rechnungen" (US-08). */
export function Billing() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const timeZone = useMe().data?.account.timezone;
  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: () => unwrap(api.GET('/billing')),
    // After Checkout the webhook activates the plan within seconds (AC ≤ 30 s).
    refetchInterval: params.get('checkout') === 'success' ? 3000 : false,
  });
  const plans = useQuery({ queryKey: ['plans'], queryFn: () => unwrap(api.GET('/billing/plans')) });
  const portal = useMutation({
    mutationFn: () => unwrap(api.POST('/billing/portal-session')),
    onSuccess: (data) => go(data.url),
  });
  if (billing.isPending || plans.isPending) return <Loading />;
  if (billing.isError)
    return (
      <LoadError message={errorMessage(billing.error, t)} onRetry={() => void billing.refetch()} />
    );
  if (plans.isError)
    return (
      <LoadError message={errorMessage(plans.error, t)} onRetry={() => void plans.refetch()} />
    );
  const b = billing.data;
  const trialDays = b.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(b.trialEndsAt).getTime() - Date.now()) / 86_400_000))
    : null;
  const tooMany = b.usage.sites > b.usage.maxSites;

  return (
    <>
      <PageTitle>{t('billing.title')}</PageTitle>
      <div className="flex flex-col gap-4">
        {params.get('checkout') === 'success' ? (
          <Notice kind={b.subscription ? 'success' : 'info'}>
            {b.subscription ? t('billing.thanks') : t('billing.activating')}
          </Notice>
        ) : null}
        {params.get('checkout') === 'cancelled' ? (
          <Notice kind="info">{t('billing.cancelled')}</Notice>
        ) : null}
        {b.subscription?.pastDueSince ? (
          <Notice kind="warning">{t('billing.pastDue')}</Notice>
        ) : null}
        {b.mode === 'invoice' && !b.live ? (
          <Notice kind="warning">{t('billing.demo')}</Notice>
        ) : null}
        <Card>
          <h2 className="mb-2 text-xl font-semibold">{t('billing.yourPlan')}</h2>
          <p className="text-2xl font-bold">{t(`admin.plans.${b.planCode}`)}</p>
          {trialDays !== null ? (
            <p className="mt-1">
              {t('billing.trial', {
                count: trialDays,
                date: formatDate(b.trialEndsAt ?? '', i18n.language, timeZone),
              })}
            </p>
          ) : null}
          {b.subscription?.provider === 'manual' ? (
            <p className="mt-1 text-slate-800">
              {t(`billing.intervals.${b.subscription.interval}`)} ·{' '}
              {b.subscription.status === 'active' && b.subscription.currentPeriodEnd
                ? t('billing.paidUntil', {
                    date: formatDate(b.subscription.currentPeriodEnd, i18n.language, timeZone),
                  })
                : t('billing.ended')}
            </p>
          ) : b.subscription ? (
            <p className="mt-1 text-slate-800">
              {t(`billing.intervals.${b.subscription.interval}`)} ·{' '}
              {b.subscription.cancelAtPeriodEnd
                ? t('billing.endsOn', {
                    date: formatDate(
                      b.subscription.currentPeriodEnd ?? '',
                      i18n.language,
                      timeZone,
                    ),
                  })
                : b.subscription.currentPeriodEnd
                  ? t('billing.renewsOn', {
                      date: formatDate(b.subscription.currentPeriodEnd, i18n.language, timeZone),
                    })
                  : null}
            </p>
          ) : null}
          <dl className="mt-3 grid gap-2 sm:grid-cols-2">
            <div>
              <dt className="text-sm text-slate-700">{t('billing.usageSites')}</dt>
              <dd className="font-semibold">
                {b.usage.activeSites} / {b.usage.maxSites}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-slate-700">{t('billing.usageRecipients')}</dt>
              <dd className="font-semibold">
                {b.usage.recipients} / {b.usage.maxRecipients}
              </dd>
            </div>
          </dl>
          {b.mode === 'stripe' ? (
            <Button
              variant="secondary"
              className="mt-3"
              busy={portal.isPending}
              disabled={!b.subscription}
              onClick={() => portal.mutate()}
            >
              {t('billing.portal')}
            </Button>
          ) : null}
          {portal.isError ? <Notice kind="error">{errorMessage(portal.error, t)}</Notice> : null}
        </Card>
        {tooMany ? <ActiveSites key={b.planCode} billing={b} /> : null}
        <PlanTable billing={b} plans={plans.data.items} />
        {b.mode === 'invoice' ? <Invoices timeZone={timeZone} /> : null}
      </div>
    </>
  );
}
